import type { Collector } from '@/types/odoo';

export interface CollectorSystemSubtotal {
  systeem: string;
  m2: number;
}

/** A group of collectoren sharing one materiaallijst (or the "geen lijst" fallback group). */
export interface MateriaallijstGroup {
  /** materiaallijst_id, or null for the unlinked fallback group. */
  materiaallijstId: number | null;
  /** WHT/OUT/… name, or null when unlinked. */
  materiaallijstName: string | null;
  collectors: Collector[];
  /** Σ m² across the group's collectoren. */
  totalM2: number;
  /** Per-system m² subtotals within the group. */
  systems: CollectorSystemSubtotal[];
}

export interface CollectorenAggregate {
  groups: MateriaallijstGroup[];
  totalCollectors: number;
  /** Σ m² across all groups (fase total). */
  totalM2: number;
  /** Per-system m² across all groups (fase total). */
  systems: CollectorSystemSubtotal[];
  /** Count of distinct materiaallijsten (excludes the unlinked fallback group). */
  linkedListCount: number;
}

function systemKey(c: Collector): string {
  const raw = c.x_studio_collector_systeem;
  return raw && raw.trim() ? raw.trim() : '';
}

function addToSystems(map: Map<string, number>, key: string, m2: number) {
  map.set(key, (map.get(key) ?? 0) + m2);
}

function systemsFromMap(map: Map<string, number>): CollectorSystemSubtotal[] {
  return Array.from(map.entries())
    .map(([systeem, m2]) => ({ systeem, m2 }))
    .sort((a, b) => b.m2 - a.m2);
}

/**
 * Group collectoren per materiaallijst with per-system subtotals + a fase total.
 * Collectoren without a materiaallijst link land in a single trailing "geen lijst"
 * group (materiaallijstId = null), matching the mockup's pre-backfill fallback.
 * Collectoren with an empty systeem are bucketed under '' so the UI can render a
 * "Systeem ?" pill while still counting their m².
 */
export function buildCollectorenAggregate(collectors: Collector[]): CollectorenAggregate {
  const groupMap = new Map<number | null, MateriaallijstGroup>();
  const faseSystems = new Map<string, number>();
  let totalM2 = 0;

  for (const c of collectors) {
    const listId = c.materiaallijst_id ?? null;
    let group = groupMap.get(listId);
    if (!group) {
      group = {
        materiaallijstId: listId,
        materiaallijstName: c.materiaallijst_name ?? null,
        collectors: [],
        totalM2: 0,
        systems: [],
      };
      groupMap.set(listId, group);
    }
    group.collectors.push(c);

    const m2 = c.x_studio_collector_m2 || 0;
    const sys = systemKey(c);
    group.totalM2 += m2;
    totalM2 += m2;
    addToSystems(faseSystems, sys, m2);
  }

  // Build per-group system subtotals + sort collectors within each group.
  for (const group of groupMap.values()) {
    const map = new Map<string, number>();
    for (const c of group.collectors) {
      addToSystems(map, systemKey(c), c.x_studio_collector_m2 || 0);
    }
    group.systems = systemsFromMap(map);
    group.collectors.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    );
  }

  // Linked groups first (by name), unlinked fallback group last.
  const groups = Array.from(groupMap.values()).sort((a, b) => {
    if (a.materiaallijstId === null) return 1;
    if (b.materiaallijstId === null) return -1;
    return (a.materiaallijstName ?? '').localeCompare(b.materiaallijstName ?? '');
  });

  const linkedListCount = groups.filter((g) => g.materiaallijstId !== null).length;

  return {
    groups,
    totalCollectors: collectors.length,
    totalM2,
    systems: systemsFromMap(faseSystems),
    linkedListCount,
  };
}
